import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, ExternalLink, FileText, Globe, Link2, Lock, Network, Plus, RefreshCw, Rocket, Settings, ShieldCheck, Timer, Trash2 } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { DomainRecord } from "@hostdock/types";
import { Button, cn, ConfirmDialog, CopyButton, Dialog, ErrorState, Field, FormError, formatDate, Input, Select, Skeleton, Tabs, toast } from "@hostdock/ui";
import { api } from "../api";
import { Cell, FilterTabs, InfoCard, InfoRow, PageHero, Pill, primaryButtonClass, SearchBox, StatCard, tableCell, Toolbar } from "../components/page-kit";
import { DOCS_HREF } from "../layout";

type Filter = "all" | "connected" | "pending" | "attention";
const DAY = 86_400_000;
const externalDocs = /^https?:\/\//.test(DOCS_HREF);

const certStatus = (d: DomainRecord) => d.certificate?.status ?? "NONE";
const expiringSoon = (d: DomainRecord) => certStatus(d) === "ISSUED" && !!d.certificate?.notAfter && new Date(d.certificate.notAfter).getTime() - Date.now() < 14 * DAY;
const needsAttention = (d: DomainRecord) => d.status === "FAILED" || ["FAILED", "EXPIRED", "EXPIRING"].includes(certStatus(d)) || expiringSoon(d);
const KIND_LABEL: Record<DomainRecord["kind"], string> = { PRIMARY: "Primary", ALIAS: "Alias", REDIRECT: "Redirect" };

const DOMAIN_RE = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,62}$/;
/** What a customer types, as a bare domain name (no scheme, path or trailing dot). */
export const cleanDomain = (v: string) => v.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");

/** The customer's domains that `name` is a subdomain of, closest first. */
export const parentsOf = (name: string, all: readonly DomainRecord[]) =>
  all.filter((d) => name.endsWith(`.${d.name}`)).sort((a, b) => b.name.length - a.name.length);

const LABEL_RE = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/;

/** Adds a domain or a subdomain of one of the customer's domains to their list. Used on the Domains page and in "Create website". */
export function AddDomainForm({ onAdded, autoFocus, initialMode = "domain", initialParent }: { onAdded(d: DomainRecord): void; autoFocus?: boolean; initialMode?: "domain" | "subdomain"; initialParent?: string }) {
  const qc = useQueryClient();
  const doms = useQuery({ queryKey: ["domains"], queryFn: () => api.domains.list({ pageSize: 100 }) });
  const parents = useMemo(() => [...new Set((doms.data?.items ?? []).map((d) => d.name).filter((n) => !n.startsWith("www.")))].sort(), [doms.data]);
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [parent, setParent] = useState(initialParent ?? "");
  const base = parent || parents[0] || "";
  const full = mode === "domain" ? name : label && base ? `${label}.${base}` : "";
  const add = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.domains.add({ name: full }),
    onSuccess: (r) => { setName(""); setLabel(""); void qc.invalidateQueries({ queryKey: ["domains"] }); void qc.invalidateQueries({ queryKey: ["dns-zones"] }); toast.success(`${r.domain.name} added${r.domain.status === "VERIFIED" && mode === "subdomain" ? " and verified" : ""}`); onAdded(r.domain); },
  });
  const ok = mode === "domain" ? DOMAIN_RE.test(name) : LABEL_RE.test(label) && !!base && DOMAIN_RE.test(full) && full.length <= 253;
  const baseVerified = doms.data?.items.some((d) => d.name === base && d.status === "VERIFIED");
  return (
    <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); if (ok) add.mutate(); }}>
      <Tabs className="mb-0 self-start" value={mode} onChange={(m) => { setMode(m); add.reset(); }} items={[{ value: "domain", label: "Domain" }, { value: "subdomain", label: "Subdomain" }]} />
      {mode === "domain" ? (
        <Field label="Domain name" hint="The domain you own, e.g. example.com. Add the DNS record shown next to prove it is yours; you can create the website straight away.">
          {(p) => <Input {...p} value={name} onChange={(e) => setName(cleanDomain(e.target.value))} placeholder="example.com" autoFocus={autoFocus} autoComplete="off" spellCheck={false} />}
        </Field>
      ) : doms.isPending ? (
        <Skeleton className="h-16 w-full rounded-lg" />
      ) : parents.length === 0 ? (
        <p className="rounded-lg border border-line bg-surface-2 p-3 text-[12.5px] text-muted">Add the domain itself first (e.g. example.com); then you can add subdomains of it like blog.example.com.</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <Field label="Subdomain" hint="Only the part before your domain, e.g. blog or shop.">
              {(p) => <Input {...p} value={label} onChange={(e) => setLabel(e.target.value.trim().toLowerCase().replace(/\.+$/, ""))} placeholder="blog" autoFocus={autoFocus} autoComplete="off" spellCheck={false} />}
            </Field>
            <Field label="Of domain">
              {(p) => <Select {...p} value={base} onChange={(e) => setParent(e.target.value)}>{parents.map((n) => <option key={n} value={n}>{n}</option>)}</Select>}
            </Field>
          </div>
          <p className="text-[12.5px] text-muted">
            {full ? <>Adds <b className="text-fg">{full}</b>. </> : null}
            {baseVerified ? <>It is verified straight away because you have verified {base}.</> : <>{base} is not verified yet, so the subdomain is verified by its own DNS record, shown after you add it.</>}
          </p>
        </>
      )}
      <FormError error={add.error} />
      <div className="flex justify-end"><Button variant="primary" type="submit" disabled={!ok} loading={add.isPending} icon={<Plus size={15} />}>{mode === "domain" ? "Add domain" : "Add subdomain"}</Button></div>
    </form>
  );
}

export function DomainsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [adding, setAdding] = useState<{ mode: "domain" | "subdomain"; parent?: string } | null>(null);
  const [removing, setRemoving] = useState<DomainRecord | null>(null);
  const verify = useMutation({
    mutationFn: (d: DomainRecord) => api.domains.verify(d.id),
    onSuccess: (d) => { void qc.invalidateQueries({ queryKey: ["domains"] }); if (d?.status === "VERIFIED") toast.success(`${d.name} is verified`); else toast.error(d?.lastCheckError ?? "Not verified yet"); },
  });
  const remove = useMutation({ meta: { inlineError: true },
    mutationFn: (d: DomainRecord) => api.domains.remove(d.id),
    onSuccess: (_r, d) => { setRemoving(null); void qc.invalidateQueries({ queryKey: ["domains"] }); void qc.invalidateQueries({ queryKey: ["websites"] }); void qc.invalidateQueries({ queryKey: ["dns-zones"] }); toast.success(`${d.name} removed`); },
  });
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["domains"], queryFn: () => api.domains.list({ pageSize: 100 }), refetchInterval: 30_000 });
  const sites = useQuery({ queryKey: ["websites"], queryFn: () => api.websites.list({ pageSize: 100 }), staleTime: 15_000 });
  const siteName = useMemo(() => new Map(sites.data?.items.map((w) => [w.id, w.primaryDomain])), [sites.data]);
  // Each domain's DNS zone in the Zone Editor (a subdomain's is its parent's).
  const zones = useQuery({ queryKey: ["dns-zones"], queryFn: () => api.dns.zones(), staleTime: 15_000 });
  const zoneOf = useMemo(() => {
    const zs = [...(zones.data?.items ?? [])].sort((a, b) => b.domain.length - a.domain.length);
    return (name: string) => zs.find((z) => name === z.domain || name.endsWith(`.${z.domain}`))?.id;
  }, [zones.data]);
  const items = q.data?.items;
  const summary = useMemo(() => {
    if (!items) return null;
    const connected = items.filter((d) => d.status === "VERIFIED").length;
    const secured = items.filter((d) => certStatus(d) === "ISSUED").length;
    const pct = (n: number) => (items.length ? Math.round((n / items.length) * 100) : 0);
    return { total: items.length, connected, pending: items.filter((d) => d.status === "PENDING_VERIFICATION").length, attention: items.filter(needsAttention).length, secured, connectedPct: pct(connected), securedPct: pct(secured) };
  }, [items]);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items?.filter((d) => (filter === "connected" ? d.status === "VERIFIED" : filter === "pending" ? d.status === "PENDING_VERIFICATION" : filter === "attention" ? needsAttention(d) : true) && (!term || d.name.toLowerCase().includes(term)));
  }, [items, filter, search]);

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero icon={Link2} title="Domains" description="Add your domains here first, then create websites on them. DNS and HTTPS status for every domain." action={
        <div className="flex flex-wrap gap-2">
          {!!items?.length && <Button icon={<Plus size={15} />} onClick={() => setAdding({ mode: "subdomain" })}>Add subdomain</Button>}
          <button type="button" className={primaryButtonClass} onClick={() => setAdding({ mode: "domain" })}><Plus size={17} strokeWidth={2.2} />Add domain</button>
        </div>
      } />

      {items && items.length > 0 && summary && (
        <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
          <StatCard icon={Globe} tile="teal" label="Total domains" value={summary.total} />
          <StatCard icon={CheckCircle2} tile="green" label="Connected" value={summary.connected} sub={`${summary.connectedPct}% pointing to HostDock`} />
          <StatCard icon={Timer} tile="orange" label="Waiting for DNS" value={summary.pending} sub={summary.pending ? "Add the DNS records below" : "Nothing waiting"} />
          <StatCard icon={ShieldCheck} tile="blue" label="HTTPS secured" value={summary.secured} sub={`${summary.securedPct}% secured`} />
        </div>
      )}

      {items && items.length > 0 && summary && (
        <Toolbar>
          <FilterTabs<Filter>
            label="Filter domains"
            value={filter}
            onChange={setFilter}
            items={[["all", `All (${summary.total})`], ["connected", `Connected (${summary.connected})`], ["pending", `Waiting for DNS (${summary.pending})`], ["attention", `Needs attention (${summary.attention})`]] as const}
          />
          <SearchBox value={search} onChange={setSearch} placeholder="Search domains..." />
        </Toolbar>
      )}

      <div className="mt-[15px]">
        {q.error ? (
          <div className="hd-card p-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        ) : q.isPending ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : !items || items.length === 0 ? (
          <div className="hd-card flex flex-col items-center px-6 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent"><Link2 size={24} /></span>
            <p className="mt-3 text-[14px] font-semibold">No domains yet</p>
            <p className="mt-1 max-w-sm text-[12.5px] text-muted">Start by adding your domain. Then create a website on it; extra aliases and redirects are added from a website's Domains tab.</p>
            <Button className="mt-4" variant="primary" icon={<Plus size={15} />} onClick={() => setAdding({ mode: "domain" })}>Add domain</Button>
          </div>
        ) : rows && rows.length === 0 ? (
          <div className="hd-card px-6 py-12 text-center">
            <p className="text-[13.5px] font-semibold">No matching domains</p>
            <p className="mt-1 text-[12.5px] text-muted">Try a different search or filter.</p>
          </div>
        ) : (
          <DomainTable
            rows={rows ?? []} all={items} siteName={siteName} zoneOf={zoneOf} open={open} onToggle={(id) => setOpen(open === id ? null : id)}
            onManage={(d) => navigate(`/websites/${d.websiteId}?tab=domains`)}
            onCreate={(d) => navigate(`/websites/new?domain=${encodeURIComponent(d.name)}`)}
            onVerify={(d) => verify.mutate(d)} verifying={verify.isPending ? verify.variables?.id ?? null : null}
            onRemove={setRemoving} onAddSub={(d) => setAdding({ mode: "subdomain", parent: d.name })}
          />
        )}
      </div>

      {items && items.length > 0 && summary && (
        <InfoRow>
          <InfoCard
            to="/dns"
            icon={Network}
            tile="bg-[#d5f4f6] text-[#0a8ea0]"
            className="bg-[#eef9fb] dark:bg-surface-2"
            title="Point your domains to HostDock"
            body={summary.pending
              ? `${summary.pending} domain${summary.pending === 1 ? " is" : "s are"} waiting for DNS. Point them at HostDock's nameservers (shown in the Zone Editor), or add the records shown for each one at your current DNS provider. HTTPS is set up automatically once DNS points here.`
              : "All your domains point to HostDock. Manage their records in the Zone Editor."}
          />
          <InfoCard to={DOCS_HREF} external={externalDocs} icon={FileText} tile="bg-[#d9eafe] text-[#2f6fd6]" className="bg-[#eff5fc] dark:bg-surface-2" title="Need help with your domains?" body="Learn how to connect a domain, add aliases and redirects, and how HTTPS certificates are issued and renewed." />
        </InfoRow>
      )}

      <Dialog open={adding !== null} onOpenChange={(o) => { if (!o) setAdding(null); }} title={adding?.mode === "subdomain" ? "Add a subdomain" : "Add a domain"} description="Add it to your list first; then create a website on it.">
        {adding && <AddDomainForm key={`${adding.mode}:${adding.parent ?? ""}`} autoFocus initialMode={adding.mode} initialParent={adding.parent} onAdded={(d) => { setAdding(null); if (d.status !== "VERIFIED") setOpen(d.id); }} />}
      </Dialog>
      <ConfirmDialog
        open={removing !== null} onOpenChange={(o) => { if (!o) setRemoving(null); }}
        title={`Remove ${removing?.name ?? "this domain"}?`}
        description={removing?.websiteId
          ? `It is taken off ${siteName.get(removing.websiteId) ?? "its website"}, which stops answering on this address, and removed from your list. You can add it again later.`
          : "It is taken off your domain list. You can add it again later."}
        confirmLabel="Remove" loading={remove.isPending} error={remove.error} onConfirm={() => removing && remove.mutate(removing)}
      />
    </div>
  );
}

function DnsPill({ d }: { d: DomainRecord }) {
  if (d.status === "VERIFIED") return <Pill tone="ok" dot>Connected</Pill>;
  if (d.status === "FAILED") return <Pill tone="bad" dot>Check failed</Pill>;
  return <Pill tone="wait" dot>Waiting for DNS</Pill>;
}

function HttpsPill({ d }: { d: DomainRecord }) {
  const s = certStatus(d);
  if (s === "ISSUED") return expiringSoon(d) ? <Pill tone="wait" icon={Lock}>Renewing</Pill> : <Pill tone="ok" icon={Lock}>Secure</Pill>;
  if (s === "FAILED") return <Pill tone="bad" icon={Lock}>Failed</Pill>;
  if (s === "EXPIRED") return <Pill tone="bad" icon={Lock}>Expired</Pill>;
  if (s === "EXPIRING") return <Pill tone="wait" icon={Lock}>Expiring</Pill>;
  if (s === "PENDING") return <Pill tone="wait" icon={Lock}>Pending</Pill>;
  return <Pill tone="off" icon={Lock}>{d.status === "VERIFIED" ? "Not issued" : "After DNS"}</Pill>;
}

function DomainTable({ rows, all, siteName, zoneOf, open, onToggle, onManage, onCreate, onVerify, verifying, onRemove, onAddSub }: {
  rows: DomainRecord[]; all: DomainRecord[]; siteName: Map<string, string>; zoneOf(name: string): string | undefined; open: string | null; onToggle(id: string): void; onManage(d: DomainRecord): void;
  onCreate(d: DomainRecord): void; onVerify(d: DomainRecord): void; verifying: string | null; onRemove(d: DomainRecord): void; onAddSub(d: DomainRecord): void;
}) {
  const cols = "xl:grid xl:grid-cols-[232px_96px_150px_124px_minmax(0,1fr)_326px] xl:items-center";
  return (
    <div className="hd-card px-[18px] pb-[3px]" role="table" aria-label="Domains">
      <div className={cn(cols, "hidden h-[40px] border-b border-line text-[9.5px] font-medium tracking-[0.06em] text-muted uppercase")} role="row">
        <span role="columnheader">Domain</span><span role="columnheader" className="pl-[17px]">Type</span><span role="columnheader" className="pl-[17px]">DNS</span><span role="columnheader" className="pl-[17px]">HTTPS</span><span role="columnheader" className="pl-[17px]">Certificate</span><span role="columnheader" className="pl-[17px]">Actions</span>
      </div>
      <ul className="divide-y divide-line" role="rowgroup">
        {rows.map((d) => {
          const pending = d.status !== "VERIFIED";
          const listed = !d.websiteId; // in the customer's list, not on a website yet
          const site = d.websiteId ? siteName.get(d.websiteId) : undefined;
          const parent = d.name.startsWith("www.") ? undefined : parentsOf(d.name, all)[0]; // www. is shown as the alias it is
          const isMain = !listed && d.kind === "PRIMARY"; // a website's main address: removed with the website
          const zoneId = zoneOf(d.name);
          const dnsBtn = zoneId ? (
            <Link to={`/dns/${zoneId}`} aria-label={`DNS records of ${d.name}`} title="DNS records (Zone Editor)"
              className="inline-flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-lg border border-line-strong bg-surface text-fg transition hover:bg-surface-2"><Network size={15} /></Link>
          ) : null;
          const removeBtn = (
            <button type="button" onClick={() => onRemove(d)} disabled={isMain} aria-label={`Remove ${d.name}`}
              title={isMain ? "This is the website's main address. Delete the website to remove it." : "Remove"}
              className="inline-flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-lg border border-line-strong bg-surface text-danger transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40"><Trash2 size={15} /></button>
          );
          return (
            <Fragment key={d.id}>
              <li role="row" aria-label={d.name} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[71px] xl:gap-0 xl:py-[11px]")}>
                <div role="cell" className="flex min-w-0 items-center gap-[14px] xl:pr-3">
                  <span className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-xl bg-[#e3f5f8] text-[#0a8ea0] dark:bg-surface-3 dark:text-accent"><Globe size={21} strokeWidth={1.7} /></span>
                  <div className="min-w-0 leading-tight">
                    <span className="flex min-w-0 items-center gap-[8px]">
                      <span className="truncate text-[14px] font-bold">{d.name}</span>
                      {!pending && <a href={`https://${d.name}`} target="_blank" rel="noreferrer" className="shrink-0 text-muted hover:text-accent" aria-label={`Open ${d.name}`} title="Open"><ExternalLink size={13.5} /></a>}
                    </span>
                    <span className="mt-[4px] flex min-w-0 flex-wrap items-center gap-x-1 text-[11.5px] text-muted">
                      {parent && <span className="truncate">Subdomain of {parent.name}</span>}
                      {parent && site && site !== d.name && <span aria-hidden>·</span>}
                      {site && site !== d.name && <Link to={`/websites/${d.websiteId}`} className="truncate hover:text-accent">on {site}</Link>}
                      {!parent && !d.name.startsWith("www.") && <>{site && site !== d.name && <span aria-hidden>·</span>}<button type="button" onClick={() => onAddSub(d)} className="font-medium text-accent hover:underline" aria-label={`Add a subdomain of ${d.name}`}>+ Subdomain</button></>}
                    </span>
                    {listed && (
                      <span className="mt-[4px] block truncate text-[11.5px] text-muted">
                        Not on a website yet
                        {pending && <> · <button type="button" aria-expanded={open === d.id} onClick={() => onToggle(d.id)} className="font-medium text-accent hover:underline">DNS setup</button></>}
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:contents">
                  <Cell label="Type" className={tableCell}>
                    <span className="text-muted">{listed ? "—" : KIND_LABEL[d.kind]}</span>
                    {d.kind === "REDIRECT" && d.redirectTo && <span className="max-w-full truncate text-[11px] text-subtle">→ {d.redirectTo}</span>}
                  </Cell>
                  <Cell label="DNS" className={tableCell}><DnsPill d={d} /></Cell>
                  <Cell label="HTTPS" className={tableCell}>{listed ? <Pill tone="off" icon={Lock}>With a website</Pill> : <HttpsPill d={d} />}</Cell>
                  <Cell label="Certificate" className={tableCell}>
                    {d.certificate?.notAfter && certStatus(d) === "ISSUED"
                      ? <><span className="max-w-full truncate whitespace-nowrap text-fg/90">Renews by {formatDate(d.certificate.notAfter)}</span>{d.certificate.issuer && <span className="max-w-full truncate text-[11px] text-subtle">{d.certificate.issuer}</span>}</>
                      : <span className="max-w-full truncate text-muted" title={d.certificate?.lastError ?? undefined}>{listed ? "Issued once a website uses it" : d.certificate?.lastError ? d.certificate.lastError : pending ? "Issued once DNS points here" : "—"}</span>}
                  </Cell>
                </div>
                <div role="cell" className="flex items-center gap-[10px] xl:h-[48px] xl:gap-[8px] xl:border-l xl:border-line xl:pl-[10px]">
                  {listed ? (
                    <>
                      <button type="button" onClick={() => onCreate(d)} className="inline-flex h-[36px] flex-1 items-center justify-center gap-[6px] rounded-lg bg-[#048d9f] px-[10px] text-[12.5px] font-medium whitespace-nowrap text-white transition hover:bg-[#037d8d] xl:flex-none"><Rocket size={15} />Create website</button>
                      {pending && <button type="button" onClick={() => onVerify(d)} disabled={verifying === d.id} aria-label={`Check DNS for ${d.name}`} title="Check DNS now" className="inline-flex h-[36px] w-[36px] items-center justify-center rounded-lg border border-line-strong bg-surface text-fg transition hover:bg-surface-2 disabled:opacity-60"><RefreshCw size={15} className={verifying === d.id ? "animate-spin" : undefined} /></button>}
                      {dnsBtn}
                      {removeBtn}
                    </>
                  ) : (<>
                  <button type="button" onClick={() => onManage(d)} className="inline-flex h-[36px] flex-1 items-center justify-center gap-[8px] rounded-lg bg-[#048d9f] px-[14px] text-[12.5px] font-medium text-white shadow-[0_2px_8px_rgb(4_141_159/0.25)] transition hover:bg-[#037d8d] xl:w-[96px] xl:flex-none"><Settings size={15} />Manage</button>
                  {pending ? (
                    <button type="button" aria-expanded={open === d.id} onClick={() => onToggle(d.id)} className="inline-flex h-[36px] flex-1 items-center justify-center gap-[6px] rounded-lg border border-line-strong bg-surface px-[12px] text-[12.5px] font-medium whitespace-nowrap text-fg transition hover:bg-surface-2 xl:w-[112px] xl:flex-none">
                      DNS setup<ChevronDown size={14} className={cn("transition-transform", open === d.id && "rotate-180")} />
                    </button>
                  ) : (
                    <a href={`https://${d.name}`} target="_blank" rel="noreferrer" className="inline-flex h-[36px] flex-1 items-center justify-center gap-[8px] rounded-lg border border-line-strong bg-surface px-[14px] text-[12.5px] font-medium text-fg transition hover:bg-surface-2 xl:w-[96px] xl:flex-none"><ExternalLink size={15} />Visit</a>
                  )}
                  {dnsBtn}
                  {removeBtn}
                  </>)}
                </div>
              </li>
              {(pending || listed) && open === d.id && <DnsSetup d={d} />}
            </Fragment>
          );
        })}
      </ul>
    </div>
  );
}

/** The records that connect a pending domain, exactly as HostDock checks for them. */
function DnsSetup({ d }: { d: DomainRecord }) {
  return (
    <li role="row" aria-label={`DNS records for ${d.name}`} className="!border-t-0 pb-4">
      <div role="cell" className="rounded-xl border border-[#f9dcc0] bg-[#fff8f0] p-4 text-[12.5px] dark:border-warning/30 dark:bg-warning-soft">
        <p className="font-semibold text-fg">Connect {d.name}</p>
        <p className="mt-1 text-muted">{d.websiteId
          ? <>Add <b>one</b> of these records where your domain's DNS is managed. HostDock checks automatically every few minutes; HTTPS is issued as soon as the domain points here.</>
          : <>Add this record where your domain's DNS is managed to prove it is yours. HostDock checks automatically every few minutes. Once a website uses the domain, its address to point the domain at is shown here.</>}</p>
        {d.lastCheckError && <p className="mt-2 text-danger">Last check: {d.lastCheckError}</p>}
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {d.verification.aRecord && (
            <Record type="A" name={d.name} value={d.verification.aRecord} hint="Points the domain at HostDock (recommended)." copy={`${d.name} A ${d.verification.aRecord}`} />
          )}
          <Record type="TXT" name={d.verification.txtName} value={d.verification.txtValue} hint="Proves ownership without moving the site yet." copy={`${d.verification.txtName} TXT "${d.verification.txtValue}"`} />
        </div>
      </div>
    </li>
  );
}

function Record({ type, name, value, hint, copy }: { type: string; name: string; value: string; hint: string; copy: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-line bg-surface p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="rounded-md bg-surface-3 px-2 py-0.5 text-[11px] font-semibold">{type} record</span>
        <CopyButton value={copy} label={`Copy ${type}`} />
      </div>
      <dl className="mt-2 grid grid-cols-[52px_minmax(0,1fr)] gap-x-2 gap-y-1 text-[12px]">
        <dt className="text-subtle">Name</dt><dd className="truncate font-mono" title={name}>{name}</dd>
        <dt className="text-subtle">Value</dt><dd className="truncate font-mono" title={value}>{value}</dd>
      </dl>
      <p className="mt-2 text-[11.5px] text-muted">{hint}</p>
    </div>
  );
}
